/**
 * The Migrations table: status on this environment, the last run, and Dry run / Run per row.
 * A row click (or History) opens that migration's runs.
 */
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { MODE_LABELS, ticketUrl } from '../constants';
import type { Migration, MigrationRun } from '../api/migrationsClient';
import { RunStateTag } from './MigrationRunsDrawer';
import { lastRunOf, runnerName } from '../format';
import { formatTime } from '../../envCopy/format';

const { Text } = Typography;

const stop = (e: React.MouseEvent) => e.stopPropagation();

/** The ticket link (when there is one) over the migration's id. */
export const MigrationName: React.FC<{ id: string; ticket: string | null }> = ({ id, ticket }) => (
  <Space direction="vertical" size={0}>
    {ticket && (
      <a href={ticketUrl(ticket)} target="_blank" rel="noreferrer" onClick={stop}>
        {ticket}
      </a>
    )}
    <Text type="secondary" style={{ fontSize: 12 }}>
      {id}
    </Text>
  </Space>
);

export const MigrationStatusTag: React.FC<{ status: string }> = ({ status }) =>
  status === 'done' ? <Tag color="green">Done</Tag> : <Tag color="red">Not run</Tag>;

/** A run's mode and outcome, then who started it and when; "Never" when there is none. */
export const LastRunCell: React.FC<{ run: MigrationRun | null }> = ({ run }) => {
  if (!run) return <Text type="secondary">Never</Text>;
  return (
    <Space direction="vertical" size={0}>
      <Space size={4} wrap>
        <Text>{MODE_LABELS[run.mode] ?? run.mode}</Text>
        <RunStateTag status={run.status} />
      </Space>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {runnerName(run)}, {formatTime(run.startedAt)}
      </Text>
    </Space>
  );
};

export interface MigrationsTableProps {
  migrations: Migration[];
  loading: boolean;
  /** Shown when the list is empty (default: no migrations at all). */
  emptyText?: string;
  /** Every action is off: a run is in progress, a start is being sent, or the list is stale. */
  actionsOff: boolean;
  onDryRun: (m: Migration) => void;
  onRun: (m: Migration) => void;
  onOpen: (m: Migration) => void;
}

export const MigrationsTable: React.FC<MigrationsTableProps> = ({
  migrations,
  loading,
  emptyText = 'No migrations in the code on this environment.',
  actionsOff,
  onDryRun,
  onRun,
  onOpen,
}) => (
  <Table<Migration>
    rowKey="id"
    size="small"
    loading={loading}
    dataSource={migrations}
    pagination={false}
    locale={{ emptyText: loading ? ' ' : emptyText }}
    onRow={(m) => ({ onClick: () => onOpen(m), style: { cursor: 'pointer' } })}
    columns={[
      {
        title: 'Migration',
        key: 'migration',
        render: (_, m) => <MigrationName id={m.id} ticket={m.ticket} />,
      },
      {
        title: 'What it does',
        key: 'description',
        render: (_, m) =>
          m.maintenance ? (
            <Space direction="vertical" size={2}>
              {/* No tag: these rows sit in their own Maintenance section. */}
              <span>{m.description}</span>
              {m.runWhen && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Run when: {m.runWhen}
                </Text>
              )}
            </Space>
          ) : (
            m.description
          ),
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, m) => <MigrationStatusTag status={m.status} />,
      },
      {
        title: 'Last run',
        key: 'last',
        render: (_, m) => <LastRunCell run={lastRunOf(m)} />,
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, m) => (
          <Space onClick={stop}>
            <Button size="small" disabled={actionsOff} onClick={() => onDryRun(m)}>
              Dry run
            </Button>
            <Tooltip
              title={
                m.canApply
                  ? undefined
                  : 'A dry run of this version must finish without errors first.'
              }
            >
              {/* The span keeps the tooltip working on a disabled button. */}
              <span>
                <Button
                  size="small"
                  type="primary"
                  danger
                  disabled={actionsOff || !m.canApply}
                  onClick={() => onRun(m)}
                >
                  Run
                </Button>
              </span>
            </Tooltip>
            <Button size="small" type="link" onClick={() => onOpen(m)}>
              History
            </Button>
          </Space>
        ),
      },
    ]}
  />
);
